import React, { useState, useEffect } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuRadioItem,
  DropdownMenuRadioGroup,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { fetchCountries, fetchCounties, CountryOption, CountyOption } from "@/services/locationService";

export interface FiltersProps {
  search: string;
  serviceTypes: string[];
  placeTypes: string[];
  experiences: string[];
  amenities: string[];
  selectedCountry: string;
  selectedCounties: string[];
  showAll: boolean;
  type: string;
  location: string | { lat: number; lng: number; radius: number };
  showFavoritesOnly: boolean;
}

interface DirectoryFiltersProps {
  filters: FiltersProps;
  onFilterChange: (filterType: string, value: string | string[] | boolean) => void;
  onClearAll?: () => void;
  onNearMe?: () => void;
}

const DirectoryFilters: React.FC<DirectoryFiltersProps> = ({
  filters,
  onFilterChange,
  onClearAll,
  onNearMe,
}) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  
  // Country/county state
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [counties, setCounties] = useState<CountyOption[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingCounties, setLoadingCounties] = useState(false);
  const [countryError, setCountryError] = useState<string | null>(null);
  const [countyError, setCountyError] = useState<string | null>(null);

  // Fetch countries on mount
  useEffect(() => {
    setLoadingCountries(true);
    fetchCountries()
      .then((data) => {
        console.log('Countries loaded:', data);
        setCountries(data);
        setLoadingCountries(false);
      })
      .catch((err) => {
        console.error('Error loading countries:', err);
        setCountryError("Failed to load countries");
        setLoadingCountries(false);
        // Set fallback data to prevent crashes
        setCountries([
          { value: 'IE', label: 'Ireland', counties: [] },
          { value: 'GB', label: 'United Kingdom', counties: [] },
          { value: 'US', label: 'United States', counties: [] }
        ]);
      });
  }, []);

  // Fetch counties when country changes
  useEffect(() => {
    if (!filters.selectedCountry) {
      setCounties([]);
      return;
    }
    setLoadingCounties(true);
    fetchCounties(filters.selectedCountry)
      .then((data) => {
        setCounties(data);
        setLoadingCounties(false);
      })
      .catch((err) => {
        setCountyError("Failed to load counties");
        setLoadingCounties(false);
      });
  }, [filters.selectedCountry]);

  // Helper function to toggle array items
  const toggleArrayItem = (array: string[], item: string) => {
    return array.includes(item)
      ? array.filter((i) => i !== item)
      : [...array, item];
  };
  
  // Handle country change and reset counties
  const handleCountryChange = (value: string) => {
    onFilterChange("selectedCountry", value);
    onFilterChange("selectedCounties", []);
  };
  
  // Toggle county selection
  const toggleCounty = (county: string) => {
    const newCounties = toggleArrayItem(filters.selectedCounties, county);
    onFilterChange("selectedCounties", newCounties);
  };
  
  // Toggle filter items
  const toggleFilterItem = (filterType: string, item: string) => {
    const currentItems = filters[filterType as keyof FiltersProps] as string[];
    
    // Special case for serviceTypes - ensure sauna is always selected
    if (filterType === "serviceTypes" && item === "sauna") {
      return; // Don't allow toggling off sauna
    }
    
    const newItems = toggleArrayItem(currentItems, item);
    onFilterChange(filterType, newItems);
  };

  // Group counties by region
  const countiesByRegion = counties.reduce((acc, county) => {
    if (!county || typeof county !== 'object') {
      console.error('Invalid county object:', county);
      return acc;
    }
    const region = county.region || "Other";
    if (!acc[region]) acc[region] = [];
    acc[region].push(county);
    return acc;
  }, {} as Record<string, CountyOption[]>);

  return (
    <div className="space-y-4">
      {/* Country and County Selection - Moved to Top */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Country Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Country
          </label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-between font-normal"
                disabled={loadingCountries}
              >
                {filters.selectedCountry
                  ? countries.find(c => c.value === filters.selectedCountry)?.label || "Select a country"
                  : loadingCountries ? "Loading..." : "Select a country"}
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[240px] bg-white">
              <DropdownMenuRadioGroup
                value={filters.selectedCountry}
                onValueChange={handleCountryChange}
              >
                {countries.map((country, idx) => {
                  if (!country || typeof country !== 'object') {
                    console.error('Invalid country object in dropdown:', country);
                    return null;
                  }
                  return (
                    <DropdownMenuRadioItem
                      key={country.value || idx}
                      value={country.value || ''}
                      className="cursor-pointer flex items-center gap-2"
                    >
                      {country.flag && (
                        <img
                          src={country.flag}
                          alt={country.label || 'Country flag'}
                          className="inline-block w-5 h-4 mr-2 object-cover rounded-sm"
                          style={{ verticalAlign: 'middle' }}
                        />
                      )}
                      {country.label || 'Unknown'}
                    </DropdownMenuRadioItem>
                  );
                })}
              </DropdownMenuRadioGroup>
              {countryError && <div className="text-red-500 text-xs mt-1 px-2">{countryError}</div>}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* County Selection and Near Me Button */}
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Counties
            </label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-between font-normal truncate"
                  disabled={loadingCounties || !filters.selectedCountry}
                >
                  {filters.selectedCounties?.length > 0
                    ? `${filters.selectedCounties.length} counties selected`
                    : loadingCounties ? "Loading..." : "All counties"}
                  <ChevronDown className="h-4 w-4 ml-2 flex-shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[240px] max-h-[300px] overflow-y-auto bg-white">
                <div className="p-2 space-y-2">
                  {Object.entries(countiesByRegion).map(([region, counties]) => (
                    <div key={region}>
                      <div className="font-semibold text-xs text-gray-500 px-2 py-1">{region}</div>
                      {counties.map((county, idx) => {
                        if (!county || typeof county !== 'object') {
                          console.error('Invalid county object in list:', county);
                          return null;
                        }
                        return (
                          <div
                            key={`${county.value}-${idx}`}
                            className="flex items-center space-x-2 rounded-md px-2 py-1.5 hover:bg-gray-100 cursor-pointer"
                            onClick={() => toggleCounty(county.value || '')}
                          >
                            <div className="h-4 w-4 border rounded-sm flex items-center justify-center bg-white">
                              {filters.selectedCounties?.includes(county.value || '') && (
                                <Check className="h-3 w-3 text-primary" />
                              )}
                            </div>
                            <span className="text-sm">{county.label || 'Unknown'}</span>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                  {countyError && <div className="text-red-500 text-xs mt-1 px-2">{countyError}</div>}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <Button
            type="button"
            variant="secondary"
            className="whitespace-nowrap h-10 px-4 bg-sauna-orange text-white hover:bg-sauna-orange/90"
            onClick={() => onNearMe && onNearMe()}
          >
            Near My Location
          </Button>
        </div>
      </div>
      
      {/* Search Input and Toggle Filters Button */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative w-full">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search saunas & plunges..."
            value={filters.search}
            onChange={(e) => onFilterChange("search", e.target.value)}
            className="w-full pl-9 border-gray-300 focus-visible:ring-sauna-blue"
          />
          {filters.search && (
            <button
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-500"
              onClick={() => onFilterChange("search", "")}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Favorites Filter */}
        <div className="flex items-center space-x-2">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.showFavoritesOnly}
              onChange={(e) => onFilterChange("showFavoritesOnly", e.target.checked)}
              className="h-4 w-4 text-sauna-orange focus:ring-sauna-orange border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700 whitespace-nowrap">Favorites Only</span>
          </label>
        </div>

        {/* Toggle Filters Button */}
        <Button
          variant="outline"
          className="flex-shrink-0"
          onClick={() => setIsFiltersOpen(!isFiltersOpen)}
        >
          <span className="sr-only sm:not-sr-only sm:ml-1">
            {isFiltersOpen ? "Hide Filters" : "Show Filters"}
          </span>
        </Button>
      </div>

      {/* Expanded Filters Section with Animation */}
      <Collapsible open={isFiltersOpen} className="w-full">
        <CollapsibleContent className="data-[state=open]:animate-[accordion-down_0.4s_ease-out] data-[state=closed]:animate-[accordion-up_0.3s_ease-out]">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 transition-all duration-500">
            {/* Sauna/Plunge Type */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h3 className="font-medium text-sauna-navy mb-3">Type</h3>
              <div className="space-y-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-between font-normal"
                    >
                      {!filters.type || filters.type === 'all' 
                        ? 'All Types' 
                        : filters.type === 'both' 
                          ? 'Sauna & Plunge' 
                          : filters.type?.charAt(0).toUpperCase() + filters.type?.slice(1)}
                      <ChevronDown className="h-4 w-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-[200px] bg-white">
                    <DropdownMenuRadioGroup
                      value={filters.type}
                      onValueChange={(value) => onFilterChange("type", value)}
                    >
                      <DropdownMenuRadioItem value="all">
                        All Types
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="sauna">
                        Sauna
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="plunge">
                        Plunge
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="both">
                        Both (Sauna & Plunge)
                      </DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            
            {/* Services */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <Accordion
                type="single"
                collapsible
                defaultValue="services"
                className="border-none"
              >
                <AccordionItem value="services" className="border-none">
                  <AccordionTrigger className="py-0 text-sauna-navy font-medium">
                    Services
                  </AccordionTrigger>
                  <AccordionContent className="pt-3">
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: "sauna", name: "Sauna", disabled: true },
                        { id: "cold-plunge", name: "Cold Plunge" },
                        { id: "ice-bath", name: "Ice Bath" },
                        { id: "steam-room", name: "Steam Room" },
                        { id: "infrared-sauna", name: "Infrared Sauna" },
                        { id: "wood-fired-sauna", name: "Wood Fired" },
                      ].map((service) => (
                        <label
                          key={service.id}
                          className={`flex items-center cursor-pointer gap-2 p-2 rounded hover:bg-gray-50 ${
                            service.disabled ? "opacity-50 cursor-not-allowed" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={filters.serviceTypes?.includes(service.id) ?? false}
                            onChange={() =>
                              !service.disabled &&
                              toggleFilterItem("serviceTypes", service.id)
                            }
                            className="rounded-sm text-sauna-orange focus:ring-sauna-orange"
                            disabled={service.disabled}
                          />
                          <span className="text-sm">{service.name}</span>
                        </label>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
            
            {/* Place Types */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <Accordion
                type="single"
                collapsible
                defaultValue="placeTypes"
                className="border-none"
              >
                <AccordionItem value="placeTypes" className="border-none">
                  <AccordionTrigger className="py-0 text-sauna-navy font-medium">
                    Place Types
                  </AccordionTrigger>
                  <AccordionContent className="pt-3">
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: "mobile", name: "Mobile" },
                        { id: "permanent", name: "Permanent" },
                        { id: "spa", name: "Spa" },
                        { id: "gym", name: "Gym" },
                        { id: "hotel", name: "Hotel" },
                        { id: "beach", name: "Beach" },
                      ].map((place) => (
                        <label
                          key={place.id}
                          className="flex items-center cursor-pointer gap-2 p-2 rounded hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={filters.placeTypes?.includes(place.id) ?? false}
                            onChange={() =>
                              toggleFilterItem("placeTypes", place.id)
                            }
                            className="rounded-sm text-sauna-blue focus:ring-sauna-blue"
                          />
                          <span className="text-sm">{place.name}</span>
                        </label>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
            
            {/* Experiences */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <Accordion
                type="single"
                collapsible
                defaultValue="experiences"
                className="border-none"
              >
                <AccordionItem value="experiences" className="border-none">
                  <AccordionTrigger className="py-0 text-sauna-navy font-medium">
                    Experiences
                  </AccordionTrigger>
                  <AccordionContent className="pt-3">
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: "guided-session", name: "Guided" },
                        { id: "private-session", name: "Private" },
                        { id: "meditation", name: "Meditation" },
                        { id: "breathwork", name: "Breathwork" },
                        { id: "group-session", name: "Group" },
                        { id: "wild-swimming", name: "Wild Swimming" },
                      ].map((exp) => (
                        <label
                          key={exp.id}
                          className="flex items-center cursor-pointer gap-2 p-2 rounded hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={filters.experiences?.includes(exp.id) ?? false}
                            onChange={() =>
                              toggleFilterItem("experiences", exp.id)
                            }
                            className="rounded-sm text-sauna-orange focus:ring-sauna-orange"
                          />
                          <span className="text-sm">{exp.name}</span>
                        </label>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
          {/* Amenities filters - Add similar logic if amenities are implemented */}

          {onClearAll && (
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-500"
              onClick={onClearAll}
            >
              Clear All
            </Button>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default DirectoryFilters;
